import { makeArticle, saintQuestionRelated } from "./learnFactory";

export const pageSaintDriveWork = makeArticle({
  slug: "can-i-drive-and-work-during-saint-tms",
  title: "Can I Drive and Work During SAINT® TMS? | Neuro Wellness",
  description:
    "SAINT® TMS does not use anesthesia, so patients can usually drive after sessions. Learn about working during treatment week, travel considerations, and daily life during SAINT®.",
  canonical: "/learn/can-i-drive-and-work-during-saint-tms",
  h1: "Can I Drive and Work During SAINT® TMS?",
  breadcrumbLabel: "Driving and Working During SAINT®",
  shortAnswer:
    "Yes, in most cases. SAINT® TMS is delivered while the patient is awake and does not use anesthesia or sedation, so patients are generally permitted to drive after sessions. However, the five-day schedule requires being at the clinic for most of each day, so working a full schedule during treatment week is usually not practical. Many patients work remotely between sessions or take the week off.",
  keyTakeaways: [
    "SAINT® does not use anesthesia, so driving after sessions is usually permitted.",
    "The five-day schedule requires being at the clinic for most of each day.",
    "Working remotely between sessions is common and feasible.",
    "A full work schedule during treatment week is usually not practical.",
    "If a side effect like significant headache occurs, do not drive until it resolves.",
  ],
  sections: [
    {
      heading: "Driving After SAINT® Sessions",
      paragraphs: [
        "Because SAINT® does not involve anesthesia or sedation and does not impair alertness, patients are generally permitted to drive themselves to and from the clinic. This is one of the main practical differences between SAINT® (and TMS generally) and treatments like ECT or SPRAVATO®, which require an escort.",
        "If you experience a side effect that affects your ability to drive safely — such as a significant headache, dizziness, or sedation — tell the treatment team and do not drive until the symptom resolves. A companion is optional, not required.",
      ],
    },
    {
      heading: "Working During Treatment Week",
      paragraphs: [
        "The five-day SAINT® schedule requires being at the clinic for most of each day, with ten sessions separated by roughly 50-minute breaks. This makes a full work schedule impractical during treatment week. However, many patients work remotely between sessions — the breaks are long enough to check email, take calls, or do focused work.",
        "Some patients take the entire week off. If you are traveling from out of town, the treatment week is typically the only time you need to be in Coral Springs. Pre-treatment evaluation and the MRI can often be arranged before you arrive.",
      ],
    },
    {
      heading: "Can Someone Travel With Me?",
      paragraphs: [
        "Yes. A family member, friend, or caregiver can accompany you during the treatment week. While not required for medical reasons, having a companion can provide emotional support and practical help, especially if you are traveling from out of town. The clinic can provide information about nearby accommodations.",
      ],
      links: [
        {
          to: "/travel-for-saint-tms",
          text: "See our travel guide for SAINT®",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "Can I drive after SAINT TMS treatment?",
      answer:
        "Yes, in most cases. SAINT® does not use anesthesia or sedation and does not impair alertness, so patients are generally permitted to drive after sessions. If you experience a side effect like a significant headache or dizziness, tell the treatment team and do not drive until it resolves.",
    },
    {
      question: "Can I work during SAINT treatment week?",
      answer:
        "Working remotely between sessions is common and feasible — the 50-minute breaks are long enough for email, calls, or focused work. A full in-person work schedule is usually not practical because you are at the clinic for most of each day. Many patients take the week off or work part-time remotely.",
    },
    {
      question: "Can someone travel with me for SAINT?",
      answer:
        "Yes. A family member, friend, or caregiver can accompany you during the treatment week. While not medically required, a companion can provide support and practical help, especially if you are traveling from out of town. The clinic can recommend nearby accommodations.",
    },
    {
      question: "Do I need someone to drive me home after SAINT?",
      answer:
        "No, a driver is not required. SAINT® does not use anesthesia or sedation, so patients can typically drive themselves. This differs from SPRAVATO® (esketamine) and ECT, which both require an escort home after each session.",
    },
    {
      question: "Can I fly after SAINT treatment?",
      answer:
        "There are no specific flight restrictions after SAINT® treatment. Because the treatment is non-invasive and does not involve anesthesia, patients can typically fly after completing the five-day course. If you are traveling from out of state, discuss your travel plans with the treatment team during your consultation.",
    },
  ],
  sources: [],
  relatedLinks: saintQuestionRelated,
});
